import { engineeringValues } from '@/mocks/engineeringValues';

function ValueCard({
  title,
  description,
  imageUrl,
  imageAlt,
  offset,
}: {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  offset: boolean;
}) {
  return (
    <div
      className="flex-1 min-w-0 flex flex-col rounded-[28px] bg-background-50 overflow-hidden"
      style={{
        transform: offset ? 'translateY(40px)' : 'translateY(0)',
        transition: 'none',
      }}
    >
      <div className="w-full p-2">
        <div className="w-full aspect-[3/2] rounded-[20px] overflow-hidden bg-secondary-50">
          <img
            src={imageUrl}
            alt={imageAlt}
            title={title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
      </div>

      <div className="px-[40px] pb-[40px] pt-6 text-center">
        <h3
          className="text-lg font-medium text-foreground-950"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {title}
        </h3>
        <p
          className="mt-3 text-[13px] leading-relaxed text-foreground-600 max-w-[260px] mx-auto"
          style={{ fontFamily: 'var(--font-body)', fontWeight: 300 }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

export default function ValuesSection() {
  return (
    <section
      className="w-full bg-secondary-50"
      aria-label="Engineering values"
      style={{ paddingTop: '180px', paddingBottom: '160px' }}
    >
      <div className="w-full px-4 md:px-[48px]">
        <div className="mb-5">
          <span
            className="inline-block px-5 py-2 rounded-lg border border-foreground-300/40 text-xs font-medium uppercase tracking-[0.06em] text-foreground-700"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            How We Build
          </span>
        </div>

        <h2
          className="text-[32px] md:text-[48px] font-bold text-foreground-950 leading-[1.15] max-w-[60%] tracking-tight"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          Engineering values that define every system we create
        </h2>

        <div className="mt-16 hidden lg:flex gap-[32px]">
          {engineeringValues.map((value, index) => (
            <ValueCard
              key={value.title}
              title={value.title}
              description={value.description}
              imageUrl={value.imageUrl}
              imageAlt={value.imageAlt}
              offset={index === 1 || index === 3}
            />
          ))}
        </div>

        <div className="mt-12 lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-6">
          {engineeringValues.map((value) => (
            <div
              key={value.title}
              className="flex flex-col rounded-[28px] bg-background-50 overflow-hidden"
            >
              <div className="w-full p-2">
                <div className="w-full aspect-[3/2] rounded-[20px] overflow-hidden bg-secondary-50">
                  <img
                    src={value.imageUrl}
                    alt={value.imageAlt}
                    title={value.title}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="px-6 pb-8 pt-5 text-center">
                <h3
                  className="text-base font-medium text-foreground-950"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {value.title}
                </h3>
                <p
                  className="mt-2 text-[13px] leading-relaxed text-foreground-600"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 300 }}
                >
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}